/**
 * /signup — Self-registration de ERP Partner.
 *
 * 3 pasos:
 *   1. Datos empresa (NIT, name, contact_email, allowed_emitter_nits, webhook_url).
 *   2. Aceptar Términos + Mandato (HTML rendered + checkbox).
 *   3. Mostrar credenciales en CLARO una sola vez (warning) + redirect login.
 *
 * Compat: la aceptación legal se hace AQUÍ (durante signup) — el flujo
 * sin auth aún. El backend espera POST /onboarding/erp-partner sin auth
 * para crear el cliente, y luego POST /admin/onboarding/accept con JWT
 * para registrar las aceptaciones forensic.
 *
 * Para v1 del MVP: el signup crea la cuenta + muestra credenciales.
 * El partner debe luego loguearse con tax-account y aceptar contratos
 * en /onboarding (page separada).
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, Copy, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'

import {
  onboardingApi,
  type RegisterERPPartnerRequest,
  type RegisterERPPartnerResponse,
} from '@/lib/api'
import { ROUTES } from '@/lib/constants'

export function SignupPage() {
  const nav = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState<RegisterERPPartnerResponse | null>(null)

  const [form, setForm] = useState<RegisterERPPartnerRequest>({
    name: '',
    legal_name: '',
    nit: '',
    contact_email: '',
    allowed_emitter_nits: [],
    webhook_url: '',
  })
  const [allowedNitsRaw, setAllowedNitsRaw] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ nit?: string; contact_email?: string }>({})

  // Validación inline pre-submit: NIT 6-15 dígitos y email con forma válida.
  const validate = (f: RegisterERPPartnerRequest): { nit?: string; contact_email?: string } => {
    const errs: { nit?: string; contact_email?: string } = {}
    if (!NIT_RE.test(f.nit)) {
      errs.nit = 'El NIT debe tener entre 6 y 15 dígitos, sin puntos ni DV.'
    }
    if (!EMAIL_RE.test(f.contact_email)) {
      errs.contact_email = 'Ingresa un email válido (ej. ops@tuerp.co).'
    }
    return errs
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs = validate(form)
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return
    setSubmitting(true)
    try {
      const allowed = allowedNitsRaw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
      const out = await onboardingApi.registerERPPartner({
        ...form,
        allowed_emitter_nits: allowed.length > 0 ? allowed : undefined,
        webhook_url: form.webhook_url || undefined,
        legal_name: form.legal_name || undefined,
      })
      setCreated(out)
      toast.success('Cuenta creada. Guarde sus credenciales antes de continuar.')
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string; error?: string } } }
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'error desconocido'
      if (e?.response?.status === 409) {
        toast.error(`NIT ya registrado: ${msg}`)
      } else {
        toast.error(`No se pudo crear: ${msg}`)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (created) {
    return <CredentialsView created={created} onDone={() => nav(ROUTES.LOGIN)} />
  }

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to={ROUTES.LANDING} className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-primary">
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Link>
          <span className="text-sm text-fg-secondary">¿Ya tienes cuenta? <Link to={ROUTES.LOGIN} className="text-brand-600 underline">Iniciar sesión</Link></span>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="text-3xl font-bold">Crear cuenta de Partner</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Registro inicial del ERP. Después del signup completarás la aceptación
          de Términos + Contrato de Mandato.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-xl border border-border bg-bg-surface p-6">
          <Field label="Nombre comercial" required>
            <input
              type="text"
              value={form.name}
              maxLength={120}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className={inputCls}
              placeholder="Acme ERP"
            />
          </Field>
          <Field label="Razón social (opcional, completo legal)">
            <input
              type="text"
              value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              className={inputCls}
              placeholder="Acme ERP Colombia S.A.S."
            />
          </Field>
          <Field label="NIT" required hint="Solo dígitos, sin DV (6-15 caracteres)" error={fieldErrors.nit}>
            <input
              type="text"
              value={form.nit}
              required
              pattern="[0-9]{6,15}"
              aria-invalid={!!fieldErrors.nit}
              onChange={(e) => {
                setForm({ ...form, nit: e.target.value })
                if (fieldErrors.nit && NIT_RE.test(e.target.value)) {
                  setFieldErrors((prev) => ({ ...prev, nit: undefined }))
                }
              }}
              onBlur={(e) => {
                if (e.target.value && !NIT_RE.test(e.target.value)) {
                  setFieldErrors((prev) => ({ ...prev, nit: 'El NIT debe tener entre 6 y 15 dígitos, sin puntos ni DV.' }))
                }
              }}
              className={inputCls}
              placeholder="900000001"
            />
          </Field>
          <Field label="Email de contacto" required error={fieldErrors.contact_email}>
            <input
              type="email"
              value={form.contact_email}
              required
              aria-invalid={!!fieldErrors.contact_email}
              onChange={(e) => {
                setForm({ ...form, contact_email: e.target.value })
                if (fieldErrors.contact_email && EMAIL_RE.test(e.target.value)) {
                  setFieldErrors((prev) => ({ ...prev, contact_email: undefined }))
                }
              }}
              onBlur={(e) => {
                if (e.target.value && !EMAIL_RE.test(e.target.value)) {
                  setFieldErrors((prev) => ({ ...prev, contact_email: 'Ingresa un email válido (ej. ops@tuerp.co).' }))
                }
              }}
              className={inputCls}
              placeholder="ops@erp.co"
            />
          </Field>
          <Field
            label="NITs autorizados a facturar"
            hint="Coma o espacio para separar. Vacío = solo el NIT del partner."
          >
            <input
              type="text"
              value={allowedNitsRaw}
              onChange={(e) => setAllowedNitsRaw(e.target.value)}
              className={inputCls}
              placeholder="900111222 900333444"
            />
          </Field>
          <Field
            label="Webhook URL (opcional)"
            hint="Debe ser https:// — recibirá eventos document.accepted, etc."
          >
            <input
              type="url"
              value={form.webhook_url}
              onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
              className={inputCls}
              placeholder="https://erp.example.co/webhooks/taxor"
            />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Creando…' : 'Crear cuenta'}
          </button>
          <p className="text-xs text-fg-muted">
            Al crear cuenta aceptas que el siguiente paso será firmar los Términos
            de Suscripción + Contrato de Mandato DIAN. Sin esa aceptación no
            podrás emitir documentos.
          </p>
        </form>
      </main>
    </div>
  )
}

// NIT colombiano sin DV: 6 a 15 dígitos (mismo formato que valida el backend).
const NIT_RE = /^[0-9]{6,15}$/
// Forma mínima razonable de un email — el backend hace la validación autoritativa.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-primary">
        {label}
        {required && <span className="ml-1 text-red-600">*</span>}
      </label>
      {hint && <p className="mt-0.5 text-xs text-fg-muted">{hint}</p>}
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}

const inputCls =
  'w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30'

// ─── Credentials reveal view (one-time) ──────────────────────────

function CredentialsView({
  created,
  onDone,
}: {
  created: RegisterERPPartnerResponse
  onDone: () => void
}) {
  const [hideKey, setHideKey] = useState(true)
  const [hideHmac, setHideHmac] = useState(true)
  const [acked, setAcked] = useState(false)

  const copy = (s: string, label: string) => {
    navigator.clipboard.writeText(s).then(() => toast.success(`${label} copiado`))
  }

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary">
      <main className="mx-auto max-w-2xl px-6 py-12">
        <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-4 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800">
          <p className="text-sm font-semibold">⚠ Credenciales mostradas UNA sola vez</p>
          <p className="mt-1 text-xs">
            Si pierde estos valores deberá regenerarlos (los anteriores quedan
            invalidados inmediatamente). Cópielos a su gestor de secretos antes
            de continuar.
          </p>
        </div>

        <div className="mt-6 space-y-4 rounded-xl border border-border bg-bg-surface p-6">
          <div>
            <p className="text-xs font-medium text-fg-muted">Client ID</p>
            <CopyRow value={created.client_id} onCopy={(v) => copy(v, 'client_id')} />
          </div>
          <div>
            <p className="text-xs font-medium text-fg-muted">API Key</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-md bg-bg-elevated px-3 py-2 font-mono text-xs">
                {hideKey ? '••••••••••••••••••••••••••••••••' : created.api_key}
              </code>
              <button type="button" onClick={() => setHideKey((v) => !v)} className="rounded-md border border-border p-2 hover:bg-bg-elevated">
                {hideKey ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button type="button" onClick={() => copy(created.api_key, 'api_key')} className="rounded-md border border-border p-2 hover:bg-bg-elevated">
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-fg-muted">HMAC Secret</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-md bg-bg-elevated px-3 py-2 font-mono text-xs">
                {hideHmac ? '••••••••••••••••••••••••••••••••' : created.hmac_secret}
              </code>
              <button type="button" onClick={() => setHideHmac((v) => !v)} className="rounded-md border border-border p-2 hover:bg-bg-elevated">
                {hideHmac ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button type="button" onClick={() => copy(created.hmac_secret, 'hmac_secret')} className="rounded-md border border-border p-2 hover:bg-bg-elevated">
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-md border border-border bg-bg-surface p-4">
          <p className="text-sm font-medium">Próximo paso:</p>
          <p className="mt-1 text-xs text-fg-secondary">{created.next_step}</p>
        </div>

        <label className="mt-6 flex items-start gap-2 text-sm">
          <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} className="mt-0.5" />
          <span>Confirmo que copié las credenciales en un lugar seguro.</span>
        </label>

        <button
          type="button"
          onClick={onDone}
          disabled={!acked}
          className="mt-4 w-full rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Check className="mr-1.5 inline h-4 w-4" />
          Continuar al login
        </button>
      </main>
    </div>
  )
}

function CopyRow({ value, onCopy }: { value: string; onCopy: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 break-all rounded-md bg-bg-elevated px-3 py-2 font-mono text-xs">{value}</code>
      <button type="button" onClick={() => onCopy(value)} className="rounded-md border border-border p-2 hover:bg-bg-elevated">
        <Copy className="h-4 w-4" />
      </button>
    </div>
  )
}
